import { Link } from 'react-router-dom';
import { CATEGORY_LABELS, formatZAR } from '@makhe/shared';
import { LoadingSkeleton } from '@/components/ui/Card';
import type { LandingShowcase } from '@/lib/landingShowcase';

export type { LandingShowcase };

export function LandingShowcaseCard({ card }: { card: LandingShowcase }) {
  const hasReviews = card.reviews > 0 && card.rating > 0;

  return (
    <Link
      to={`/service/${card.id}`}
      className="group relative flex flex-col overflow-hidden rounded-lg border border-makhe-line bg-makhe-paper shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-lift"
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-makhe-night">
        {card.video ? (
          <video
            src={card.video}
            autoPlay
            muted
            loop
            playsInline
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : card.img ? (
          <img
            src={card.img}
            alt={`${card.title} by ${card.sellerName}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-end bg-makhe-brand/80 p-3">
            <span className="font-display text-lg leading-tight text-makhe-on">{card.sellerName}</span>
          </div>
        )}
        <span className="absolute left-2.5 top-2.5 rounded-full bg-makhe-bg/90 px-2.5 py-1 text-[10px] uppercase tracking-widest text-makhe-ink">
          {CATEGORY_LABELS[card.category]}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <p className="truncate text-sm font-semibold leading-none">{card.sellerName}</p>
        <p className="line-clamp-2 text-xs leading-snug text-makhe-muted">{card.title}</p>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <span className="text-xs font-semibold text-makhe-ink">
            from {formatZAR(card.priceZARcents)}
          </span>
          {hasReviews ? (
            <span className="shrink-0 text-[11px] text-makhe-ochre">
              ★ {card.rating.toFixed(1)}{' '}
              <span className="text-makhe-muted">({card.reviews})</span>
            </span>
          ) : (
            <span className="shrink-0 text-[11px] uppercase tracking-widest text-makhe-muted">
              New
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function LandingShowcaseSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3" aria-hidden>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-lg border border-makhe-line bg-makhe-paper">
          <LoadingSkeleton className="aspect-[4/5] rounded-none" />
          <div className="space-y-2 p-3">
            <LoadingSkeleton className="h-3 w-2/3" />
            <LoadingSkeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
